import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getStageFromScore } from '../data/prochaskaQuestions';
import { Compass, ArrowRight, Home, RefreshCw, CheckCircle2 } from 'lucide-react';

export const ProchaskaResults = () => {
  const location = useLocation();
  const { language, t } = useLanguage();
  const result = location.state?.result;

  if (!result) {
    return <Navigate to="/dashboard" replace />;
  }

  const stageInfo = getStageFromScore(result.total_score, language);

  const stages = [
    { id: 'precontemplation', name_fr: 'Précontemplation', name_en: 'Precontemplation', color: 'bg-red-200' },
    { id: 'contemplation', name_fr: 'Contemplation', name_en: 'Contemplation', color: 'bg-orange-200' },
    { id: 'preparation', name_fr: 'Préparation', name_en: 'Preparation', color: 'bg-yellow-200' },
    { id: 'action', name_fr: 'Action', name_en: 'Action', color: 'bg-green-200' },
    { id: 'maintenance', name_fr: 'Maintien', name_en: 'Maintenance', color: 'bg-emerald-200' },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="prochaska-results">
      <div className="max-w-3xl mx-auto">
        {/* Result Card */}
        <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-xl animate-scale-in" data-testid="result-card">
          {/* Header */}
          <div className={`${stageInfo.color} p-8 sm:p-12 text-center`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/30 backdrop-blur rounded-full mb-6">
              <Compass className={`w-5 h-5 ${stageInfo.textColor}`} />
              <span className={`font-medium ${stageInfo.textColor}`}>
                {language === 'fr' ? 'Votre stade de changement' : 'Your stage of change'}
              </span>
            </div>

            <h2 className={`text-3xl sm:text-4xl font-extrabold ${stageInfo.textColor} mb-4`}>
              {language === 'fr' ? stageInfo.name_fr : stageInfo.name_en}
            </h2>
          </div>

          {/* Content */}
          <div className="p-8 sm:p-12">
            {/* Description */}
            <div className="mb-8">
              <p className="text-gray-700 text-lg leading-relaxed">
                {language === 'fr' ? stageInfo.description_fr : stageInfo.description_en}
              </p>
            </div>

            {/* Stage Progress */}
            <div className="mb-8">
              <h4 className="font-semibold text-gray-900 mb-4">
                {language === 'fr' ? 'Les 5 stades du changement' : 'The 5 stages of change'}
              </h4>
              <div className="flex items-center gap-2">
                {stages.map((stage, index) => (
                  <div key={stage.id} className="flex-1">
                    <div 
                      className={`h-3 rounded-full ${
                        stage.id === stageInfo.stage 
                          ? stage.color + ' ring-2 ring-offset-2 ring-gray-400' 
                          : 'bg-gray-200'
                      }`}
                    />
                    <p className={`text-xs mt-2 text-center ${
                      stage.id === stageInfo.stage ? 'font-semibold text-gray-900' : 'text-gray-500'
                    }`}>
                      {language === 'fr' ? stage.name_fr : stage.name_en}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Advice */}
            <div className="bg-teal-50 rounded-2xl p-6 mb-8">
              <h4 className="font-semibold text-teal-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                {language === 'fr' ? 'Conseils pour cette étape' : 'Advice for this stage'}
              </h4>
              <ul className="space-y-3">
                {(language === 'fr' ? stageInfo.advice_fr : stageInfo.advice_en).map((advice, index) => (
                  <li key={index} className="flex items-start gap-2 text-teal-800">
                    <span className="text-teal-500 mt-1">•</span>
                    {advice}
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/dashboard" className="flex-1">
                <Button 
                  variant="outline" 
                  className="w-full h-12 rounded-full border-2"
                  data-testid="back-dashboard-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  {t('backToDashboard')}
                </Button>
              </Link>
              <Link to="/prochaska-test" className="flex-1">
                <Button 
                  className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700 shadow-lg"
                  data-testid="retake-test-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Refaire le test' : 'Retake test'}
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
